import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, RefreshCw, CheckCircle, XCircle, Mail, Trash2, ExternalLink, Video, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import ZoomHealthPanel from './ZoomHealthPanel';

/**
 * Admin → Class Schedules section.
 * Pure-presentational shell: data + handlers come from AdminDashboard props.
 */
export const SchedulesSection = ({
  // data
  students, teachers,
  schedules, scheduledClasses, rescheduleRequests,
  // create dialog
  createScheduleOpen, setCreateScheduleOpen,
  scheduleType, setScheduleType,
  newSchedule, setNewSchedule,
  savingSchedule, handleCreateSchedule,
  toggleDay,
  // actions
  handleRescheduleAction,
  handleDeleteSchedule,
  handleMarkComplete,
  fetchScheduleData,
  // helpers / constants
  getCurrentTimeInTZ,
  WEEKDAYS,
}) => (
  <>
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Class Schedules</h1>
        <p className="text-slate-500 font-semibold">Manage recurring and one-time class schedules</p>
      </div>
      <Dialog open={createScheduleOpen} onOpenChange={setCreateScheduleOpen}>
        <DialogTrigger asChild>
          <Button className="bg-gray-50 border border-blue-400/40 text-blue-700 font-bold hover:bg-blue-50" data-testid="create-schedule-btn">
            <Plus className="w-4 h-4 mr-2" /> New Schedule
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-white border border-blue-200 shadow-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">Create Schedule</DialogTitle>
            <DialogDescription className="text-gray-600">Set up recurring or one-time class schedules</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Button variant={scheduleType === 'recurring' ? 'default' : 'outline'} size="sm" onClick={() => setScheduleType('recurring')} className={scheduleType === 'recurring' ? 'bg-blue-600' : ''} data-testid="schedule-type-recurring">Recurring</Button>
              <Button variant={scheduleType === 'one-time' ? 'default' : 'outline'} size="sm" onClick={() => setScheduleType('one-time')} className={scheduleType === 'one-time' ? 'bg-blue-600' : ''} data-testid="schedule-type-onetime">One-Time</Button>
            </div>
            <div>
              <Label>Student</Label>
              <Select value={newSchedule.student_id} onValueChange={v => setNewSchedule(p => ({...p, student_id: v}))}>
                <SelectTrigger data-testid="schedule-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teacher</Label>
              <Select value={newSchedule.teacher_id} onValueChange={v => setNewSchedule(p => ({...p, teacher_id: v}))}>
                <SelectTrigger data-testid="schedule-teacher"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {scheduleType === 'recurring' && (
              <div>
                <Label className="mb-2 block">Days of Week</Label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map(day => (
                    <button key={day} onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${newSchedule.days_of_week.includes(day) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-400 font-semibold border-slate-200 hover:border-blue-400'}`}
                      data-testid={`day-${day}`}>
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {scheduleType === 'one-time' && (
              <div>
                <Label>Date</Label>
                <Input type="date" value={newSchedule.date} onChange={e => setNewSchedule(p => ({...p, date: e.target.value}))} className="mt-1" data-testid="schedule-date" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Time</Label>
                <Input type="time" value={newSchedule.time} onChange={e => setNewSchedule(p => ({...p, time: e.target.value}))} className="mt-1" data-testid="schedule-time" />
              </div>
              <div>
                <Label>Timezone</Label>
                <Select value={newSchedule.timezone} onValueChange={v => setNewSchedule(p => ({...p, timezone: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IST">IST (India)</SelectItem>
                    <SelectItem value="EST">EST (US East)</SelectItem>
                    <SelectItem value="PST">PST (US West)</SelectItem>
                    <SelectItem value="GMT">GMT (London)</SelectItem>
                    <SelectItem value="SGT">SGT (Singapore)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-blue-500 mt-1" data-testid="current-tz-time">Current time in {newSchedule.timezone}: {getCurrentTimeInTZ(newSchedule.timezone)}</p>
              </div>
            </div>
            <Button onClick={handleCreateSchedule} disabled={savingSchedule} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-schedule-btn">
              {savingSchedule ? 'Creating...' : 'Create Schedule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>

    {/* Stats */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <div className="glass-card rounded-xl p-5 text-center">
        <div className="text-3xl font-bold text-blue-600">{schedules.length}</div>
        <div className="text-sm text-slate-500 font-semibold">Recurring Schedules</div>
      </div>
      <div className="glass-card rounded-xl p-5 border-2 border-emerald-100 text-center">
        <div className="text-3xl font-bold text-emerald-600">{scheduledClasses.length}</div>
        <div className="text-sm text-slate-500 font-semibold">Upcoming Classes</div>
      </div>
      <div className="glass-card rounded-xl p-5 border-2 border-amber-100 text-center">
        <div className="text-3xl font-bold text-amber-600">{rescheduleRequests.filter(r => r.status === 'pending').length}</div>
        <div className="text-sm text-slate-500 font-semibold">Pending Reschedules</div>
      </div>
    </div>

    {/* Zoom Health & Backfill */}
    <ZoomHealthPanel onBackfilled={fetchScheduleData} />

    {/* Reschedule Requests */}
    {rescheduleRequests.filter(r => r.status === 'pending').length > 0 && (
      <div className="bg-amber-500/10 rounded-xl border-2 border-amber-400/30 p-5 mb-8">
        <h2 className="font-bold text-black mb-3 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-amber-600" /> Reschedule Requests</h2>
        <div className="space-y-3">
          {rescheduleRequests.filter(r => r.status === 'pending').map(req => (
            <div key={req.id} className="glass-card rounded-lg p-4 border flex items-center justify-between" data-testid={`reschedule-req-${req.id}`}>
              <div>
                <p className="font-medium text-sm">{req.requester_name} <span className="text-xs text-slate-500 font-semibold">({req.requested_by})</span></p>
                <p className="text-xs text-slate-500 font-semibold">Reason: {req.reason || 'N/A'}</p>
                {req.proposed_date && <p className="text-xs text-blue-600">Proposed: {req.proposed_date} at {req.proposed_time}</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handleRescheduleAction(req.id, 'approve')} data-testid={`approve-${req.id}`}><CheckCircle className="w-3 h-3 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-400/30 text-xs" onClick={() => handleRescheduleAction(req.id, 'reject')} data-testid={`reject-${req.id}`}><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Recurring Schedules */}
    {schedules.length > 0 && (
      <div className="glass-card rounded-xl mb-8 overflow-hidden">
        <div className="px-6 py-4 border-b bg-blue-500/10"><h2 className="font-bold text-blue-700 font-bold">Recurring Schedules</h2></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Student</TableHead>
              <TableHead className="font-semibold">Teacher</TableHead>
              <TableHead className="font-semibold">Days</TableHead>
              <TableHead className="font-semibold">Time</TableHead>
              <TableHead className="font-semibold">Zoom Link</TableHead>
              <TableHead className="font-semibold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.map(s => (
              <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                <TableCell className="font-medium">{s.student_name}</TableCell>
                <TableCell>{s.teacher_name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">{(s.days_of_week || []).map(d => (
                    <span key={d} className="px-2 py-0.5 bg-blue-100 text-blue-600 font-bold text-xs rounded-full">{d.slice(0, 3)}</span>
                  ))}</div>
                </TableCell>
                <TableCell>{s.time} {s.timezone}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Input
                      type="url"
                      placeholder="Add Zoom link..."
                      defaultValue={s.zoom_link || ''}
                      className="text-xs h-8 w-48"
                      data-testid={`zoom-input-${s.id}`}
                      onBlur={async (e) => {
                        const link = e.target.value;
                        if (link !== (s.zoom_link || '')) {
                          try {
                            await api.put(`/admin/schedules/${s.id}/zoom-link`, { zoom_link: link });
                            toast.success('Zoom link updated');
                            fetchScheduleData();
                          } catch { toast.error('Failed to update zoom link'); }
                        }
                      }}
                    />
                    {s.zoom_link && <a href={s.zoom_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-bold"><ExternalLink className="w-3.5 h-3.5" /></a>}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700" onClick={async () => {
                      try { const res = await api.post(`/admin/schedule/${s.id}/send-email`); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }} data-testid={`email-schedule-${s.id}`} title="Email schedule to parent"><Mail className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteSchedule(s.id)} data-testid={`delete-schedule-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )}

    {/* Upcoming Scheduled Classes */}
    <div className="glass-card rounded-xl overflow-x-auto">
      <div className="px-6 py-4 border-b bg-blue-500/10"><h2 className="font-bold text-blue-700 font-bold">Upcoming Scheduled Classes</h2></div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">Date</TableHead>
            <TableHead className="font-semibold">Time</TableHead>
            <TableHead className="font-semibold">Student</TableHead>
            <TableHead className="font-semibold">Teacher</TableHead>
            <TableHead className="font-semibold">Type</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="font-semibold text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scheduledClasses.length === 0 ? (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-500 font-semibold py-8">No scheduled classes</TableCell></TableRow>
          ) : scheduledClasses.map(cls => (
            <TableRow key={cls.id} data-testid={`sched-class-${cls.id}`}>
              <TableCell className="font-medium">{cls.date}</TableCell>
              <TableCell>{cls.time} {cls.timezone}</TableCell>
              <TableCell>{cls.student_name}</TableCell>
              <TableCell>{cls.teacher_name}</TableCell>
              <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls.schedule_type === 'recurring' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-violet-100 text-violet-700'}`}>{cls.schedule_type}</span></TableCell>
              <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : cls.status === 'cancelled' ? 'bg-red-100 text-red-700' : cls.status === 'cancelled_late' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>{cls.status === 'cancelled_late' ? 'late cancel' : cls.status}</span></TableCell>
              <TableCell className="text-center">
                {cls.status === 'scheduled' && cls.date <= (() => { const _n = new Date(); return `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`; })() && (
                  <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-800 text-xs" onClick={() => handleMarkComplete(cls.id)} data-testid={`complete-${cls.id}`}><CheckCircle className="w-4 h-4 mr-1" /> Done</Button>
                )}
                {(cls.status === 'cancelled_late' || cls.is_late_cancel) && (
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 text-xs" onClick={async () => {
                    try {
                      await api.post(`/admin/classes/${cls.id}/restore-credit`);
                      toast.success('Credit restored — session no longer counts as used');
                      fetchScheduleData();
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to restore credit'); }
                  }} data-testid={`restore-${cls.id}`}><RefreshCw className="w-4 h-4 mr-1" /> Restore</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </>
);

export default SchedulesSection;
